// API Utility für das neue Admin-Dashboard mit Clerk-Authentifizierung
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from './apiConfig';

const API_BASE_URL = getApiUrl('/admin');

class AdminAPI {
  // Clerk-Token aus dem aktuellen Session abrufen
  static getClerkToken() {
    // Für Development verwenden wir einen festen Token
    // In Production sollte hier das echte Clerk-Token abgerufen werden
    if (process.env.NODE_ENV === 'development') {
      return 'dev-clerk-token';
    }
    
    // TODO: Hier sollte das echte Clerk-Token abgerufen werden
    // const { getToken } = useAuth();
    // return getToken();
    
    return null;
  }

  // Generic API call method mit Clerk-Authentifizierung
  static async request(endpoint, options = {}, token = null) {
    const url = getApiUrl(`/admin${endpoint}`);
    const authToken = token || this.getClerkToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Kursverwaltung API mit Token-Parameter
  static async getCourses(token) {
    return this.request('/courses', {}, token);
  }

  static async getCourse(id, token) {
    return this.request(`/courses/${id}`, {}, token);
  }

  static async createCourse(courseData, token) {
    return this.request('/courses', {
      method: 'POST',
      body: JSON.stringify(courseData),
    }, token);
  }

  static async updateCourse(id, courseData, token) {
    return this.request(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(courseData),
    }, token);
  }

  static async deleteCourse(id, token) {
    return this.request(`/courses/${id}`, {
      method: 'DELETE',
    }, token);
  }

  static async toggleCourseActive(id, token) {
    return this.request(`/courses/${id}/toggle-active`, {
      method: 'POST',
    }, token);
  }

  static async getCourseBookings(id, filters = {}, token) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/courses/${id}/bookings${queryParams ? `?${queryParams}` : ''}`, {}, token);
  }

  // Buchungsverwaltung API mit Token-Parameter
  static async getBookings(filters = {}, token) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/bookings${queryParams ? `?${queryParams}` : ''}`, {}, token);
  }

  static async getBooking(id, token) {
    return this.request(`/bookings/${id}`, {}, token);
  }

  static async createBooking(bookingData, token) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    }, token);
  }

  static async updateBooking(id, bookingData, token) {
    return this.request(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(bookingData),
    }, token);
  }

  static async deleteBooking(id, token) {
    return this.request(`/bookings/${id}`, {
      method: 'DELETE',
    }, token);
  }

  static async updateBookingStatus(id, status, reason, token) {
    return this.request(`/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    }, token);
  }

  static async getBookingStatistics(token) {
    return this.request('/bookings/statistics', {}, token);
  }

  static async exportBookingsCSV(filters = {}, token) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/bookings/export/csv${queryParams ? `?${queryParams}` : ''}`, {}, token);
  }

  // Statistiken API mit Token-Parameter
  static async getDashboardStats(token) {
    return this.request('/stats', {}, token);
  }
}

export default AdminAPI;
